import * as React from 'react';
import {routes as assetTrackingRoutes} from 'components/Pages/AssetTracking/routes';
import {routes as schedulingRoutes} from 'components/Pages/Scheduling/routes';
import {routes as endpointMonitoringRoutes} from 'components/Pages/EndpointMonitoring/routes';
import {useUsersMe} from 'components/Hooks/queries/useUsersMe';
import {IRouterMap} from 'utils/router';
import {useFeatureFlags} from 'components/Hooks/queries/useFeatureFlags';

export const PERMISSION_ALIAS_SCHEDULING_RESERVATIONS =
  'TruckScheduling.Reservations';
export const PERMISSION_ALIAS_SCHEDULING_MAINTENANCE =
  'TruckScheduling.MaintenanceBreaks';

export const PERMISSION_ALIAS_ASSET_TRACKING_ASSETS = 'AssetTracking.Assets';

export const PERMISSION_ALIAS_APP_ADMIN = 'App.Administrate';
export const PERMISSION_ALIAS_END_POINT_MONITORING = 'EndpointMonitoring';

export type TPermissionAliasTypes =
  | typeof PERMISSION_ALIAS_SCHEDULING_RESERVATIONS
  | typeof PERMISSION_ALIAS_SCHEDULING_MAINTENANCE
  | typeof PERMISSION_ALIAS_END_POINT_MONITORING
  | typeof PERMISSION_ALIAS_APP_ADMIN
  | typeof PERMISSION_ALIAS_ASSET_TRACKING_ASSETS;

export function generateReadRoleByAlias(
  permissionAlias: TPermissionAliasTypes,
): string {
  return `${permissionAlias}.Read`;
}

export function generateCreateRoleByAlias(
  permissionAlias: TPermissionAliasTypes,
): string {
  return `${permissionAlias}.Create`;
}

export function generateUpdateRoleByAlias(
  permissionAlias: TPermissionAliasTypes,
): string {
  return `${permissionAlias}.Update`;
}

export function generateDeleteRoleByAlias(
  permissionAlias: TPermissionAliasTypes,
): string {
  return `${permissionAlias}.Delete`;
}

export function generateAdminRoleByAlias(
  permissionAlias: TPermissionAliasTypes,
): string {
  return `${permissionAlias}.Admin`;
}
export function generateAppAdminRoleByAlias(): string {
  return PERMISSION_ALIAS_APP_ADMIN;
}

export function generateUpdateCompletedRoleByAlias(
  permissionAlias: TPermissionAliasTypes,
): string {
  return `${permissionAlias}.UpdateCompleted`;
}
export function generateUpdatePastAllFieldsRoleByAlias(
  permissionAlias: TPermissionAliasTypes,
): string {
  return `${permissionAlias}.UpdatePastAllFields`;
}

const isPathMain = (path: string) => {
  const params = path.split('/');
  return params.length === 1;
};

const isPathFirstParam = (path: string, param: string) => {
  const params = path.split('/');
  return params[1] === param;
};

const isPathCreate = (path: string) => {
  return isPathFirstParam(path, 'create');
};
const isPathView = (path: string) => {
  return isPathFirstParam(path, 'view');
};
const isPathEdit = (path: string) => {
  return isPathFirstParam(path, 'edit');
};

export type IPermissionAlias = Nullable<TPermissionAliasTypes>;

export interface IRoute {
  path: string;
  render: () => JSX.Element;
}

interface IRoutes {
  schedulingRoutes: IRouterMap[];
  endpointMonitoringRoutes: IRouterMap[];
}
export interface IPermissionContext {
  getRoutes: () => IRoutes;
  isReadable: (permissionAlias: IPermissionAlias) => boolean;
  isUpdatable: (permissionAlias: IPermissionAlias) => boolean;
  isDeletable: (permissionAlias: IPermissionAlias) => boolean;
  isCreatable: (permissionAlias: IPermissionAlias) => boolean;
  isUpdatableCompleted: (permissionAlias: IPermissionAlias) => boolean;
  isUpdatablePastAll: (permissionAlias: IPermissionAlias) => boolean;
  isPermit: (customPermission: string, allowNoScopes: boolean) => boolean;

  pagePermissionAlias: Nullable<
    TPermissionAliasTypes[] | TPermissionAliasTypes
  >;
  setPagePermissionAlias: (
    permissionAlias: Nullable<TPermissionAliasTypes[] | TPermissionAliasTypes>,
  ) => void;
}

const PermissionContext = React.createContext<IPermissionContext>({
  isPermit: () => true,
  isCreatable: () => true,
  isReadable: () => true,
  isUpdatable: () => true,
  isDeletable: () => true,
  isUpdatableCompleted: () => true,
  isUpdatablePastAll: () => true,
  getRoutes: () => {
    return {
      schedulingRoutes,
      endpointMonitoringRoutes,
    };
  },
  pagePermissionAlias: null,
  setPagePermissionAlias: (
    permissionAlias: Nullable<TPermissionAliasTypes[] | TPermissionAliasTypes>,
  ) => undefined,
});

export function usePermission(): IPermissionContext {
  const context = React.useContext<IPermissionContext>(PermissionContext);
  if (!context) {
    throw new Error(`usePermission must be used within a PermissionProvider`);
  }
  return context;
}

interface IProvider {
  children: React.ReactNode;
}

export type Nullable<T> = T | null | undefined;
export function PermissionProvider(props: IProvider): JSX.Element {
  const {data: me, isLoading} = useUsersMe();
  const {data: featureFlagsData, isLoading: isLoadingFeatureFlag} =
    useFeatureFlags(!!me);
  const [pagePermissionAlias, setPagePermissionAlias] =
    React.useState<Nullable<TPermissionAliasTypes[] | TPermissionAliasTypes>>(
      null,
    );

  function isPermit(permissionId: string, allowNoScopes: boolean): boolean {
    return (me?.permissions ?? []).some(
      ({id, scopes}) =>
        id === permissionId && (allowNoScopes || scopes?.length),
    );
  }
  function isAdmin(permissionAlias: IPermissionAlias) {
    const allowNoScopes = isAllowNoScopes(permissionAlias);
    return permissionAlias
      ? isPermit(generateAdminRoleByAlias(permissionAlias), allowNoScopes)
      : false;
  }

  // Note we check for Alias.Admin, Alias.Read/Create/Update/Delete and just Alias
  // in the below functions
  // The "just Alias" case happens for the App.Administrate permission;
  // it does not have these "sub-permissions" like App.Administrate.Read etc.

  function isReadable(permissionAlias: IPermissionAlias): boolean {
    const allowNoScopes = isAllowNoScopes(permissionAlias);
    return permissionAlias
      ? isAdmin(permissionAlias) ||
          isPermit(generateReadRoleByAlias(permissionAlias), allowNoScopes) ||
          isPermit(permissionAlias, allowNoScopes)
      : false;
  }
  function isCreatable(permissionAlias: IPermissionAlias): boolean {
    const allowNoScopes = isAllowNoScopes(permissionAlias);
    return permissionAlias
      ? isAdmin(permissionAlias) ||
          isPermit(generateCreateRoleByAlias(permissionAlias), allowNoScopes) ||
          isPermit(permissionAlias, allowNoScopes)
      : false;
  }
  function isUpdatable(permissionAlias: IPermissionAlias): boolean {
    const allowNoScopes = isAllowNoScopes(permissionAlias);
    return permissionAlias
      ? isAdmin(permissionAlias) ||
          isPermit(generateUpdateRoleByAlias(permissionAlias), allowNoScopes) ||
          isPermit(permissionAlias, allowNoScopes)
      : false;
  }
  function isDeletable(permissionAlias: IPermissionAlias): boolean {
    const allowNoScopes = isAllowNoScopes(permissionAlias);
    return permissionAlias
      ? isAdmin(permissionAlias) ||
          isPermit(generateDeleteRoleByAlias(permissionAlias), allowNoScopes) ||
          isPermit(permissionAlias, allowNoScopes)
      : false;
  }

  function isUpdatableCompleted(permissionAlias: IPermissionAlias): boolean {
    const allowNoScopes = isAllowNoScopes(permissionAlias);
    return permissionAlias
      ? isUpdatable(permissionAlias) &&
          isPermit(
            generateUpdateCompletedRoleByAlias(permissionAlias),
            allowNoScopes,
          )
      : false;
  }

  function isUpdatablePastAll(permissionAlias: IPermissionAlias): boolean {
    const allowNoScopes = isAllowNoScopes(permissionAlias);
    return permissionAlias
      ? isUpdatable(permissionAlias) &&
          isPermit(
            generateUpdatePastAllFieldsRoleByAlias(permissionAlias),
            allowNoScopes,
          )
      : false;
  }

  function isFeatureFlagEnabled(route: IRouterMap): boolean {
    if (!route.requiredFeatureFlag) {
      return true;
    }

    return (
      (featureFlagsData?.featureFlags &&
        featureFlagsData.featureFlags[route.requiredFeatureFlag]) ??
      false
    );
  }

  function isAllowNoScopes(permissionAlias: IPermissionAlias): boolean {
    return (
      permissionAlias === PERMISSION_ALIAS_APP_ADMIN ||
      permissionAlias === PERMISSION_ALIAS_ASSET_TRACKING_ASSETS
    );
  }

  const filterPermissionRoutes = (
    routes: IRouterMap[],
    permissionAlias: TPermissionAliasTypes[] | TPermissionAliasTypes,
  ): IRouterMap[] => {
    const allowedRoutes = routes.filter(route => {
      const pathNoFirstSlash = route.path.slice(1);

      const aliases = Array.isArray(permissionAlias)
        ? permissionAlias
        : [permissionAlias];

      const featureFlagEnabled = isFeatureFlagEnabled(route);

      const readRolePermit =
        (isPathMain(pathNoFirstSlash) || isPathView(pathNoFirstSlash)) &&
        aliases.some(alias => isReadable(alias));

      const createRolePermit =
        isPathCreate(pathNoFirstSlash) &&
        aliases.some(alias => isCreatable(alias));

      const updateRolePermit =
        isPathEdit(pathNoFirstSlash) &&
        aliases.some(alias => isUpdatable(alias));

      return (
        featureFlagEnabled &&
        (readRolePermit || createRolePermit || updateRolePermit)
      );
    });
    return allowedRoutes;
  };

  const routes = {
    assetTrackingRoutes: filterPermissionRoutes(
      assetTrackingRoutes,
      PERMISSION_ALIAS_ASSET_TRACKING_ASSETS,
    ),
    schedulingRoutes: filterPermissionRoutes(schedulingRoutes, [
      PERMISSION_ALIAS_SCHEDULING_RESERVATIONS,
      PERMISSION_ALIAS_SCHEDULING_MAINTENANCE,
    ]),
    endpointMonitoringRoutes: filterPermissionRoutes(
      endpointMonitoringRoutes,
      PERMISSION_ALIAS_END_POINT_MONITORING,
    ),
  };
  const value = {
    getRoutes: () => {
      return routes;
    },
    isPermit,
    isReadable,
    isCreatable,
    isUpdatable,
    isDeletable,
    isUpdatableCompleted,
    isUpdatablePastAll,
    pagePermissionAlias,
    setPagePermissionAlias,
  };

  return (
    <PermissionContext.Provider value={value}>
      {isLoading || isLoadingFeatureFlag ? null : props.children}
    </PermissionContext.Provider>
  );
}
